import type { ComponentProps, ReactNode } from 'react';

import { useTheme } from '@emotion/react';
import { ErrorMessage } from '@hookform/error-message';
import { isNil, values } from 'ramda';

import { isString } from '@twistopayments/design-system/utils';

import { Typography } from '../typography';

import * as styles from './form-helper-text.styles';

type Props = {
  children?: ReactNode;
  name: string;
  errors?: ComponentProps<typeof ErrorMessage>['errors'];
  className?: string;
  isError?: boolean;
  disabled?: boolean;
};

type ContentProps = {
  children: NonNullable<ReactNode>;
  isError?: boolean;
};

export const FormHelperText = ({
  children,
  className,
  isError = false,
  disabled = false,
  errors = {},
  name,
}: Props) => {
  const theme = useTheme();

  const Content = ({ children, isError = true }: ContentProps) => (
    <div
      className={className}
      css={[styles.root(theme), isError && styles.error(theme), disabled && styles.disabled(theme)]}
      {...(isError ? { 'data-helper-error-text': children.toString() } : undefined)}
      data-test-id="form-helper-text">
      <Typography variant="b3">{children}</Typography>
    </div>
  );

  return Object.values(errors).length ? (
    <ErrorMessage
      errors={errors}
      name={name}
      render={({ message, messages = {} }) => {
        const errorMessages = [
          ...(message ? [message] : []),
          ...values(messages).flat().filter(isString),
        ];

        return (
          <>
            {errorMessages.map((message, idx) => (
              <Content key={idx}>
                {errorMessages.length > 1 && <>{`• `}</>}
                {message}
              </Content>
            ))}
          </>
        );
      }}
    />
  ) : !isNil(children) ? (
    <Content isError={isError}>{children}</Content>
  ) : null;
};
